/**
 * RoleTemplateSuggestions - soft, optional "start from a template" row shown in
 * the Create Role modals.
 *
 * The platform seeds a set of role archetypes (Viewer, Registrations Operator,
 * Dealer, Repairer, ...) as `PermissionGroup` rows. Picking one at create time
 * seeds the new role with that archetype's permissions (via `apply_group_ids`),
 * which the admin can then fine-tune. These are suggestions, never required -
 * the create action stays enabled with nothing selected.
 */

import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import type { PermissionGroup } from "@/admin/lib/permissions";

// The admin catch-all group is used to seed built-in Admin roles, not as a
// user-facing "start from" template.
const HIDDEN_TEMPLATE_NAMES = new Set(["Full Access"]);

interface RoleTemplateSuggestionsProps {
  groups: PermissionGroup[];
  /** Currently selected template id, or null for "blank role". */
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  disabled?: boolean;
  /** Restrict to templates suitable for external (partner) roles. */
  externalOnly?: boolean;
}

export function RoleTemplateSuggestions({
  groups,
  selectedId,
  onSelect,
  disabled = false,
  externalOnly = false,
}: RoleTemplateSuggestionsProps) {
  const { t } = useTranslation();
  const suggestions = groups
    .filter((g) => !HIDDEN_TEMPLATE_NAMES.has(g.name))
    .filter((g) => !externalOnly || g.is_suitable_for_external_assignment);

  if (suggestions.length === 0) return null;

  const selected = suggestions.find((g) => g.id === selectedId) ?? null;

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {t("roles.startFromTemplate", "Start from a template")}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((group) => {
          const isSelected = group.id === selectedId;
          return (
            <Button
              key={group.id}
              type="button"
              variant="ghost"
              disabled={disabled}
              title={group.description}
              onClick={() => onSelect(isSelected ? null : group.id)}
              className={cn(
                "inline-flex h-auto items-center gap-1 rounded-[4px] border-[0.5px] px-2.5 py-[5px] text-[11px] transition-all duration-150",
                isSelected
                  ? "border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]"
                  : "border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-2)] hover:border-[var(--ink-3)] hover:text-[var(--ink)]",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              {isSelected && <Check className="h-3 w-3" />}
              {group.name}
            </Button>
          );
        })}
      </div>
      <p className="min-h-[15px] text-[11px] leading-snug text-[var(--ink-3)]">
        {selected
          ? selected.description
          : t(
              "roles.templateSuggestionsFallback",
              "Optional. Seeds the role's starting permissions - you can adjust them after.",
            )}
      </p>
    </div>
  );
}
